import { DateTime } from 'luxon';

import { ON_RPM, ON_SERVER } from '../consts.js';
import { _makeFetch, _toJson } from './_common.js';

export { PharosApiError, PharosApiUnknownError } from './_common.js';

function _getApiUrl() {
  if (ON_SERVER) {
    return `http://localhost:8080/`;
  } else {
    if (ON_RPM) {
      return `${document.location.origin}/pharosapis/v1`;
    } else {
      return `${document.location.origin}/r/pharos/api/v1`;
    }
  }
}

export const API_URL = _getApiUrl();
const DEFAULT_LIMIT = 30;

function makePropertyDataPath(propertyId) {
  return ON_RPM ? `/property/${propertyId}/cwv/` : '/';
}

function _entryToParamReducer(params, entry) {
  const [key, value] = entry;
  params.set(key, value);
  return params;
}

function _makeUrlWithSearch(urlString, searchParamsObject) {
  const url = new URL(urlString);
  const searchParams = Object.entries(searchParamsObject).reduce(
    _entryToParamReducer,
    url.searchParams
  );

  url.search = searchParams.toString();
  return url.href;
}

function _fetchGet(fullUrl) {
  return _makeFetch(fullUrl, {
    method: 'GET',
    credentials: 'include',
  }).then(_toJson);
}

function _makeGetRequest(url, searchParams) {
  return _fetchGet(_makeUrlWithSearch(url, searchParams));
}

function _makePaginationObject(offset, limit) {
  return {
    offset: offset || 0,
    limit: limit || DEFAULT_LIMIT,
  };
}

function _makePaginatedGetRequest(url, offset, limit) {
  return _makeGetRequest(url, _makePaginationObject(offset, limit));
}

export function getMyOrganizations(offset, limit) {
  return _makePaginatedGetRequest(
    `${API_URL}/organization/list`,
    offset,
    limit
  );
}

function _makeJsonPostRequest(url, body) {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  return _makeFetch(url, {
    headers,
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

export function initialSetup(link) {
  return _makeJsonPostRequest(`${API_URL}/me/initialsetup`, { link });
}

export function getAccountData() {
  return _fetchGet(`${API_URL}/me/`);
}

export function getPropertyDetails(propertyId) {
  return _fetchGet(`${API_URL}/property/${propertyId}`);
}

export function getMyProperties(offset, limit) {
  return _makePaginatedGetRequest(`${API_URL}/me/properties`, offset, limit);
}

function hackyGetLast30AvailableDates() {
  if (ON_SERVER) {
    return new Promise(() => {});
  }

  const today = DateTime.now().toUTC().startOf('day');

  function getDayDate(_value, index) {
    return today
      .minus({ day: index + 1 })
      .startOf('day')
      .toMillis();
  }

  return Promise.resolve([
    today.toMillis(),
    ...Array(29).fill(null).map(getDayDate),
  ]);
}

export function makePropertyCruxEndpoints(propertyId) {
  const root = `${API_URL}${makePropertyDataPath(propertyId)}crux`;

  function getAvailableDates(_offset, _limit) {
    return hackyGetLast30AvailableDates();
  }

  function getLatestRecordForFormFactor(formFactor) {
    return _makeGetRequest(`${root}/latest/device/record.json`, { formFactor });
  }

  function getLatestRecordsForFormFactor(formFactor, offset, limit) {
    const baseQuery = { ..._makePaginationObject(offset, limit), formFactor };
    return _makeGetRequest(`${root}/latest/device/records.json`, baseQuery);
  }

  function getLatestStatus() {
    return _makeGetRequest(`${root}/latest/status.json`, {});
  }

  function getLatestsUrls(formFactor, offset, limit) {
    const baseQuery = { ..._makePaginationObject(offset, limit), formFactor };
    return _makeGetRequest(`${root}/latest/urls.json`, baseQuery);
  }

  function getUrlsOnDate(date, formFactor, offset, limit) {
    const baseQuery = {
      ..._makePaginationObject(offset, limit),
      formFactor,
      date,
    };
    return _makeGetRequest(`${root}/date/urls.json`, baseQuery);
  }

  function getTimeframeStatus(from, to) {
    return _makeGetRequest(`${root}/timeframe/status.json`, { from, to });
  }

  function getLatestUrlStatus(url) {
    return _makeGetRequest(`${root}/latest/url.json`, { url });
  }

  function getLatestBadUrls(formFactor, offset, limit) {
    const baseQuery = { ..._makePaginationObject(offset, limit), formFactor };
    return _makeGetRequest(`${root}/latest/bad/urls.json`, baseQuery);
  }

  return {
    getAvailableDates,
    getLatestStatus,
    getLatestsUrls,
    getUrlsOnDate,
    getTimeframeStatus,
    getLatestUrlStatus,
    getLatestRecordForFormFactor,
    getLatestRecordsForFormFactor,
    getLatestBadUrls,
  };
}

function rpmPropertyDebugEndpointsMaker(scope, idKey) {
  function makeEndpoints(propertyId) {
    const root = `${API_URL}${makePropertyDataPath(propertyId)}${scope}`;

    function getAvailableDates(_offset, _limit) {
      return hackyGetLast30AvailableDates();
    }

    function getLatestIssues(offset, limit) {
      return _makePaginatedGetRequest(
        `${root}/latest/issues.json`,
        offset,
        limit
      );
    }

    function getIssuesOnDate(date, offset, limit) {
      const baseQuery = { ..._makePaginationObject(offset, limit), date };
      return _makeGetRequest(`${root}/date/issues.json`, baseQuery);
    }

    function getLatestIssueDetails(identifier) {
      return _makeGetRequest(`${root}/latest/issue/details.json`, {
        [idKey]: identifier,
      });
    }

    function getIssueDetailsOnDate(identifier, date) {
      return _makeGetRequest(`${root}/date/issue/details.json`, {
        [idKey]: identifier,
        date,
      });
    }

    const endpoints = {
      getAvailableDates,
      getLatestIssues,
      getIssuesOnDate,
      getLatestIssueDetails,
      getIssueDetailsOnDate,
    };

    return endpoints;
  }

  return makeEndpoints;
}

export const makePropertyInpEndpoints = rpmPropertyDebugEndpointsMaker(
  'inp',
  'signature'
);

export const makePropertyInpUrlEndpoints = rpmPropertyDebugEndpointsMaker(
  'inpurl',
  'url'
);

export const makePropertyLcpEndpoints = rpmPropertyDebugEndpointsMaker(
  'lcp',
  'signature'
);

export const makePropertyLcpUrlEndpoints = rpmPropertyDebugEndpointsMaker(
  'lcpurl',
  'url'
);

export const makePropertyClsEndpoints = rpmPropertyDebugEndpointsMaker(
  'cls',
  'signature'
);

export const makePropertyClsUrlEndpoints = rpmPropertyDebugEndpointsMaker(
  'clsurl',
  'url'
);

export function makeOrganizationPropertiesEndpoints(organizationId) {
  const root = `${API_URL}/organization/${organizationId}/properties`;

  function list(offset, limit) {
    return _makePaginatedGetRequest(`${root}/list`, offset, limit);
  }

  function create(link) {
    return _makeJsonPostRequest(`${root}/create`, { link });
  }

  return { list, create };
}

export function makeOrganizationMembersEndpoints(organizationId) {
  const root = `${API_URL}/organization/${organizationId}/members`;
  function list(offset, limit) {
    return _makePaginatedGetRequest(`${root}/list`, offset, limit);
  }

  function invite(email) {
    return _makeJsonPostRequest(`${root}/invite`, { email });
  }

  return { list, invite };
}

export function makePropertyRpmEndpoints(propertyId) {
  const root = `${API_URL}${makePropertyDataPath(propertyId)}rpm/cwv`;

  function getLatestOriginDataForFormFactorAndFrequency(
    formFactor,
    frequency,
    offset,
    limit
  ) {
    return _makePaginatedGetRequest(
      `${root}/origin/latest/${formFactor}/${frequency}/data.json`,
      offset,
      limit
    );
  }

  function getRangedOriginDataForFormFactorAndFrequency(
    formFactor,
    frequency,
    from,
    to,
    offset,
    limit
  ) {
    const queryParams = {
      ..._makePaginationObject(offset, limit),
      from,
      to,
    };

    return _makeGetRequest(
      `${root}/origin/timeframe/${formFactor}/${frequency}/data.json`,
      queryParams
    );
  }

  function getAvailableDatesForFormFactorAndFrequency(_formFactor, _frequency) {
    return hackyGetLast30AvailableDates();
  }

  function getAnalyzedUrlsOnDateForFormFactorAndFrequency(
    date,
    formFactor,
    frequency,
    offset,
    limit
  ) {
    const baseQuery = { ..._makePaginationObject(offset, limit), date };
    return _makeGetRequest(
      `${root}/urls/date/${formFactor}/${frequency}/data.json`,
      baseQuery
    );
  }

  function getLatestAnalyzedUrlsForFormFactorAndFrequency(
    formFactor,
    frequency,
    offset,
    limit
  ) {
    return _makePaginatedGetRequest(
      `${root}/urls/latest/${formFactor}/${frequency}/data.json`,
      offset,
      limit
    );
  }

  function getLatestBadUrlsForFormFactorAndFrequency(
    formFactor,
    frequency,
    offset,
    limit
  ) {
    return _makePaginatedGetRequest(
      `${root}/urls/bad/latest/${formFactor}/${frequency}/data.json`,
      offset,
      limit
    );
  }

  function getLatestDataForUrlFormFactorAndFrequency(
    formFactor,
    frequency,
    url,
    offset,
    limit
  ) {
    const query = { ..._makePaginationObject(offset, limit), url };
    return _makeGetRequest(
      `${root}/url/latest/${formFactor}/${frequency}/data.json`,
      query
    );
  }

  return {
    getLatestOriginDataForFormFactorAndFrequency,
    getRangedOriginDataForFormFactorAndFrequency,
    getAvailableDatesForFormFactorAndFrequency,
    getAnalyzedUrlsOnDateForFormFactorAndFrequency,
    getLatestAnalyzedUrlsForFormFactorAndFrequency,
    getLatestDataForUrlFormFactorAndFrequency,
    getLatestBadUrlsForFormFactorAndFrequency,
  };
}

export async function getHackAdminUserList() {
  return _makeGetRequest(`${API_URL}/hacks/userList`, {});
}
